// frontend/src/dashboard/SettingsPage.tsx
//
// Worqly *workspace* settings — the proper /dashboard/settings page.
//
// This replaces the previous mount which was actually the invoice-prefs
// page from `modules/invoicing/pages/SettingsPage.tsx`. That page still
// exists (and is still mounted at /dashboard/settings/invoicing), but
// users coming from the sidebar or user-menu "Workspace settings" link
// were getting an invoicing-only screen — this fixes the UX confusion.
//
// Layout
//   ┌───────────────────────────────────────────────────────────────┐
//   │ Sticky left section nav      │ Section content (GlassCards)   │
//   │  · Profile                    │  ┌──────────────────────────┐  │
//   │  · Company                    │  │ Form rows + per-section  │  │
//   │  · Team                       │  │ Save button              │  │
//   │  · Modules                    │  └──────────────────────────┘  │
//   │  · Security                   │                                │
//   │  · Notifications              │                                │
//   │  · Danger zone                │                                │
//   └───────────────────────────────────────────────────────────────┘
//
// Backend wiring
//   · Profile name update      — no PATCH /api/auth/me yet, stubbed.
//   · Company save             — PATCH /api/tenant/settings (real).
//   · Team list                — GET   /api/hr/employees     (real).
//   · Modules                  — GET   /api/billing/subscription (real).
//   · Password change          — magic-link reset; stubbed CTA.
//   · Notifications            — localStorage (no backend yet).
//   · Delete workspace         — stub; confirms via name typing, then toast.
//
// Light + dark mode verified for every surface.

import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  UserRound, Building2, Users as UsersIcon, Boxes, ShieldCheck,
  BellRing, AlertTriangle, ArrowUpRight, Check, ExternalLink,
} from 'lucide-react';
import { useAuth } from '../core/auth/AuthProvider';
import { usePermission } from '../core/auth/usePermission';
import { MfaSettingsCard } from '../core/auth/MfaSettingsCard';
import { useBranding } from '../core/branding/BrandingProvider';
import { useEnabledModules } from '../core/modules/EnabledModulesProvider';
import { useApi } from '../design-system/useApi';
import { api } from '../core/api-client';
import { PageHeader } from '../design-system/components/PageHeader';
import { LoadingState } from '../design-system/components/LoadingState';
import {
  GlassCard, Pill, Eyebrow, Avatar,
  Button as PdsButton,
  Input as PdsInput,
  Textarea as PdsTextarea,
  FieldLabel,
} from '../design-system/pds';
import { ImageUploadInput } from '../design-system/components/ImageUploadInput';
import PaymentInstructionsEditor, { PaymentInstructionsValue } from './PaymentInstructionsEditor';
import { useConfirm } from '../components/ui/confirm-dialog';
import { formatInTimeZone } from '../design-system/format';
import { cn } from '../lib/utils';
import type { EmployeeWithUser } from '../modules/hr/types';

/* ─────────────────────────── timezone list ─────────────────────────── */

// Curated common IANA timezones for the workspace selector. The server
// accepts ANY valid IANA zone, so this is purely a friendly shortlist —
// the current value is injected even if it's not in the list.
const COMMON_TIMEZONES: string[] = [
  'UTC',
  'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
  'America/Sao_Paulo',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
  'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos',
  'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dhaka',
  'Asia/Bangkok', 'Asia/Singapore', 'Asia/Shanghai', 'Asia/Tokyo',
  'Australia/Sydney', 'Pacific/Auckland',
];

/* ─────────────────────────── section model ─────────────────────────── */

type SectionId =
  | 'profile' | 'company' | 'team' | 'modules' | 'security' | 'notifications' | 'danger';

interface SectionMeta {
  id: SectionId;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const SECTIONS: SectionMeta[] = [
  { id: 'profile',       label: 'Profile',         icon: <UserRound className="h-4 w-4" />,   description: 'Your account inside this workspace.' },
  { id: 'company',       label: 'Company',         icon: <Building2 className="h-4 w-4" />,   description: 'Identity shown on invoices and emails.' },
  { id: 'team',          label: 'Team',            icon: <UsersIcon className="h-4 w-4" />,   description: 'People who can sign in.' },
  { id: 'modules',       label: 'Modules',         icon: <Boxes className="h-4 w-4" />,       description: 'Enabled features for this workspace.' },
  { id: 'security',      label: 'Security',        icon: <ShieldCheck className="h-4 w-4" />, description: 'Password, sessions and device access.' },
  { id: 'notifications', label: 'Notifications',   icon: <BellRing className="h-4 w-4" />,    description: 'Where and when we ping you.' },
  { id: 'danger',        label: 'Danger zone',     icon: <AlertTriangle className="h-4 w-4" />, description: 'Irreversible workspace actions.' },
];

/* ────────────────────────────── page ────────────────────────────── */

export function SettingsPage() {
  const { tenant } = useBranding();
  const { modules } = useEnabledModules();
  // Deep-linkable sections: `?section=company` lands straight on that section.
  // Without this the onboarding walkthrough could never spotlight anything
  // outside Profile — its anchor simply is not mounted. Anything unrecognised
  // falls back to Profile.
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('section');
  const initialSection: SectionId = SECTIONS.some((s) => s.id === requested)
    ? (requested as SectionId)
    : 'profile';
  const [active, setActive] = React.useState<SectionId>(initialSection);

  // A navigation to ?section=… while this page is already mounted (React Router
  // keeps the same element for a search-only change) must still move sections,
  // otherwise "Show me" does nothing when you are already on Settings.
  React.useEffect(() => {
    if (requested && SECTIONS.some((s) => s.id === requested)) setActive(requested as SectionId);
  }, [requested]);

  // The Danger-zone backend (DELETE /api/tenant) gates on
  // userHasPermission('access.manage') — owners fast-pass. Mirror that gate
  // exactly so members never see a section whose actions would 403.
  const canManageWorkspace = usePermission('access.manage');
  const sections = React.useMemo(
    () => (canManageWorkspace ? SECTIONS : SECTIONS.filter((s) => s.id !== 'danger')),
    [canManageWorkspace],
  );

  // Wait for tenant before rendering forms — they bind to its current values.
  if (!tenant) return <LoadingState variant="page" />;

  return (
    <div>
      <PageHeader
        eyebrow="Workspace"
        title="Settings"
        description="Your account, company identity, team and workspace-wide preferences. Invoice-specific defaults live in their own settings page."
        actions={
          modules.has('invoicing') && (
            <Link
              to="/dashboard/settings/invoicing"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white/80 dark:bg-white/[0.08] text-ink dark:text-white text-[12.5px] font-semibold shadow-pill ring-1 ring-ink/[0.08] dark:ring-white/10 hover:bg-white dark:hover:bg-white/[0.14] transition-colors"
            >
              Invoicing-specific settings
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          )
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-14 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8 lg:gap-10 max-w-6xl">
        <SectionNav sections={sections} active={active} onSelect={setActive} />
        <div className="min-w-0 space-y-6">
          {active === 'profile'       && <ProfileSection />}
          {active === 'company'       && <CompanySection />}
          {active === 'team'          && <TeamSection />}
          {active === 'modules'       && <ModulesSection />}
          {active === 'security'      && <SecuritySection />}
          {active === 'notifications' && <NotificationsSection />}
          {active === 'danger'        && canManageWorkspace && <DangerZoneSection />}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── left nav ─────────────────────────── */

function SectionNav({ sections, active, onSelect }: { sections: SectionMeta[]; active: SectionId; onSelect: (id: SectionId) => void }) {
  return (
    <nav
      aria-label="Settings sections"
      className="lg:sticky lg:top-24 self-start"
    >
      <ul className="space-y-1">
        {sections.map((s) => {
          const isActive = s.id === active;
          const danger   = s.id === 'danger';
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSelect(s.id)}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'group w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-left transition-colors',
                  'text-[13px] font-medium tracking-[-0.005em]',
                  isActive
                    ? danger
                      ? 'bg-rose-500/12 text-rose-700 dark:text-rose-300 ring-1 ring-rose-500/25'
                      : 'bg-ink-900 text-white shadow-[0_8px_22px_-10px_rgba(0,0,0,0.45)] dark:bg-coral'
                    : danger
                      ? 'text-rose-700/80 dark:text-rose-300/80 hover:bg-rose-500/8'
                      : 'text-ink/70 dark:text-white/70 hover:text-ink dark:hover:text-white hover:bg-white/55 dark:hover:bg-white/[0.06]',
                )}
              >
                <span className="flex h-5 w-5 items-center justify-center shrink-0">{s.icon}</span>
                <span className="truncate">{s.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/* ─────────────────────────── shared bits ─────────────────────────── */

function SectionHeader({
  title, description, extra,
}: { title: string; description: string; extra?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
      <div className="min-w-0">
        <h2 className="font-sans font-medium text-title-lg text-ink dark:text-white tracking-[-0.01em]">
          {title}
        </h2>
        <p className="text-[13px] text-ink/60 dark:text-white/55 mt-1.5 leading-relaxed max-w-2xl">
          {description}
        </p>
      </div>
      {extra}
    </div>
  );
}

function SaveBar({
  dirty, saving, onSave, savedLabel = 'All changes saved', 'data-tour': dataTour,
}: { dirty: boolean; saving: boolean; onSave: () => void; savedLabel?: string; 'data-tour'?: string }) {
  return (
    <div className="flex items-center justify-between mt-7 pt-5 border-t border-ink/8 dark:border-white/10">
      <p className="text-[12.5px] text-ink/55 dark:text-white/55">
        {dirty ? 'Unsaved changes' : savedLabel}
      </p>
      <PdsButton
        size="md"
        onClick={onSave}
        disabled={saving || !dirty}
        className="!h-10 !px-5 !text-[13px]"
        data-tour={dataTour}
      >
        {saving ? 'Saving…' : 'Save changes'}
      </PdsButton>
    </div>
  );
}

/* ─────────────────────────── PROFILE ─────────────────────────── */

function ProfileSection() {
  const { identity } = useAuth();
  const userName  = identity?.type === 'user' ? identity.name  : '';
  const userEmail = identity?.type === 'user' ? identity.email : '';

  const [name, setName] = React.useState<string>(userName);
  const [saving, setSaving] = React.useState(false);
  const dirty = name.trim() !== (userName ?? '').trim();

  React.useEffect(() => { setName(userName); }, [userName]);

  const { refresh } = useAuth();

  async function save() {
    if (!dirty) return;
    setSaving(true);
    try {
      await api('/api/auth/me', { method: 'PATCH', body: { name: name.trim() } });
      await refresh();
      toast.success('Profile updated');
    } catch (err: any) {
      toast.error('Save failed', { description: err.message ?? String(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <SectionHeader
        title="Profile"
        description="The name and email tied to your sign-in."
      />
      <GlassCard padding="p-6 sm:p-8">
        <div className="flex items-center gap-4 mb-7">
          <Avatar name={userName || userEmail} size="xl" />
          <div className="min-w-0">
            <p className="text-[15px] font-semibold text-ink dark:text-white tracking-[-0.005em] truncate">
              {userName || 'You'}
            </p>
            <p className="text-[12.5px] text-ink/55 dark:text-white/55 truncate">{userEmail}</p>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor="profile-name">Full name</FieldLabel>
            <PdsInput
              id="profile-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
            />
          </div>
          <div>
            <FieldLabel htmlFor="profile-email">Email</FieldLabel>
            <PdsInput
              id="profile-email"
              value={userEmail}
              readOnly
              disabled
              className="opacity-70 cursor-not-allowed"
            />
            <p className="text-[11.5px] text-ink/50 dark:text-white/45 mt-1.5">
              Read-only. Changing your sign-in email isn't supported yet — contact support if you need it changed.
            </p>
          </div>
        </div>

        <SaveBar dirty={dirty} saving={saving} onSave={save} />
      </GlassCard>
    </section>
  );
}

/* ─────────────────────────── COMPANY ─────────────────────────── */

interface CompanyDraft {
  company_name?: string;
  company_tagline?: string | null;
  company_address?: string | null;
  company_phone?: string | null;
  company_email?: string | null;
  company_website?: string | null;
  company_logo_url?: string | null;
  company_accent_color?: string | null;
  timezone?: string;
  invoice_default_terms?: string | null;
  payment_instructions?: PaymentInstructionsValue | null;
}

function CompanySection() {
  const { tenant, reloadTenant } = useBranding();
  const [draft, setDraft] = React.useState<CompanyDraft>({});
  const [saving, setSaving] = React.useState(false);

  function val<K extends keyof CompanyDraft>(k: K): string {
    if (k in draft) return (draft[k] as string) ?? '';
    const t = tenant as any;
    return t?.[k] ?? '';
  }
  function set<K extends keyof CompanyDraft>(k: K, v: CompanyDraft[K]) {
    setDraft((d) => ({ ...d, [k]: v }));
  }

  // payment_instructions is an object, not a string — `val()` above is typed
  // for the string fields, so it gets its own accessor.
  const paymentInstructions: PaymentInstructionsValue | null =
    'payment_instructions' in draft
      ? (draft.payment_instructions ?? null)
      : (((tenant as any)?.payment_instructions as PaymentInstructionsValue | undefined) ?? null);

  const dirty = Object.keys(draft).length > 0;

  async function save() {
    if (!dirty) return;
    setSaving(true);
    try {
      await api('/api/tenant/settings', { method: 'PATCH', body: draft });
      toast.success('Company info saved');
      setDraft({});
      reloadTenant();
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <SectionHeader
        title="Company"
        description="Identity shown on invoice PDFs, outbound email and your dashboard sidebar."
        extra={
          <Link
            to="/dashboard/settings/integrations"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white/80 dark:bg-white/[0.08] text-ink dark:text-white text-[12.5px] font-semibold shadow-pill ring-1 ring-ink/[0.08] dark:ring-white/10 hover:bg-white dark:hover:bg-white/[0.14] transition-colors"
          >
            Integrations
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        }
      />
      <GlassCard padding="p-6 sm:p-8">
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <ImageUploadInput
              value={val('company_logo_url')}
              onChange={(url) => set('company_logo_url', url || null)}
              endpoint="tenant"
              label="Company logo"
              helperText="Shown on invoices, the customer portal, and emails."
            />
          </div>

          <div>
            <FieldLabel htmlFor="co-name">Company name</FieldLabel>
            <PdsInput
              id="co-name"
              value={val('company_name')}
              onChange={(e) => set('company_name', e.target.value)}
              placeholder="Acme Inc."
            />
          </div>
          <div>
            <FieldLabel htmlFor="co-tagline">Tagline</FieldLabel>
            <PdsInput
              id="co-tagline"
              value={val('company_tagline')}
              onChange={(e) => set('company_tagline', e.target.value || null)}
              placeholder="Shown under your company name"
            />
          </div>

          <div className="sm:col-span-2">
            <FieldLabel htmlFor="co-address">Address</FieldLabel>
            <PdsTextarea
              id="co-address"
              data-tour="workspace.details"
              rows={2}
              value={val('company_address')}
              onChange={(e) => set('company_address', e.target.value || null)}
              placeholder="Street, City, State, ZIP"
            />
          </div>

          <div>
            <FieldLabel htmlFor="co-email">Billing email</FieldLabel>
            <PdsInput
              id="co-email"
              type="email"
              value={val('company_email')}
              onChange={(e) => set('company_email', e.target.value || null)}
              placeholder="billing@yourcompany.com"
            />
          </div>
          <div>
            <FieldLabel htmlFor="co-phone">Phone</FieldLabel>
            <PdsInput
              id="co-phone"
              value={val('company_phone')}
              onChange={(e) => set('company_phone', e.target.value || null)}
              placeholder="+1 (555) 010-0000"
            />
          </div>
          <div>
            <FieldLabel htmlFor="co-website">Website</FieldLabel>
            <PdsInput
              id="co-website"
              value={val('company_website')}
              onChange={(e) => set('company_website', e.target.value || null)}
              placeholder="https://yourcompany.com"
            />
          </div>
          <div>
            <FieldLabel htmlFor="co-accent">Accent color (hex)</FieldLabel>
            <PdsInput
              id="co-accent"
              value={val('company_accent_color')}
              onChange={(e) => set('company_accent_color', e.target.value || null)}
              placeholder="#0066FF"
            />
          </div>

          <div>
            <FieldLabel htmlFor="co-timezone">Workspace timezone</FieldLabel>
            <TimezoneSelect
              id="co-timezone"
              value={val('timezone') || 'UTC'}
              onChange={(tz) => set('timezone', tz)}
            />
            <p className="text-[11.5px] text-ink/50 dark:text-white/45 mt-1.5">
              Used for dates and times shown across the workspace.
            </p>
          </div>

          <div className="sm:col-span-2">
            <FieldLabel htmlFor="co-invoice-terms">Invoice terms</FieldLabel>
            <PdsTextarea
              id="co-invoice-terms"
              rows={7}
              value={val('invoice_default_terms')}
              onChange={(e) => set('invoice_default_terms', e.target.value || null)}
              placeholder="e.g. Payment due within 30 days. Late payments subject to a 1.5% monthly fee."
            />
            <p className="text-[11.5px] text-ink/50 dark:text-white/45 mt-1.5">
              Added to every new invoice. You can edit or remove it on any individual invoice.
            </p>
          </div>
        </div>

        <div className="mt-7">
          <h3 className="text-[14px] font-semibold text-ink dark:text-white">Payment instructions</h3>
          <p className="text-[12.5px] text-ink/55 dark:text-white/55 mt-1 mb-4">
            Shown as a "How to pay" section on invoices that still owe money.
          </p>
          <PaymentInstructionsEditor
            value={paymentInstructions}
            onChange={(v) => set('payment_instructions', v)}
          />
        </div>

        <SaveBar dirty={dirty} saving={saving} onSave={save} />
      </GlassCard>
    </section>
  );
}

/** Native <select> of curated IANA timezones, styled to match PdsInput.
 *  Injects the current value at the top if it isn't already in the shortlist. */
function TimezoneSelect({
  id, value, onChange,
}: { id?: string; value: string; onChange: (tz: string) => void }) {
  const options = React.useMemo(() => {
    const list = [...COMMON_TIMEZONES];
    if (value && !list.includes(value)) list.unshift(value);
    return list;
  }, [value]);

  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'h-11 w-full rounded-2xl px-3.5 text-[13.5px] text-ink dark:text-white',
        'bg-white/70 dark:bg-white/[0.05] ring-1 ring-ink/10 dark:ring-white/10',
        'focus:outline-none focus:ring-2 focus:ring-coral/40 transition-shadow',
      )}
    >
      {options.map((tz) => (
        <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
      ))}
    </select>
  );
}

/* ─────────────────────────── TEAM ─────────────────────────── */

function TeamSection() {
  const employeesQ = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const employees = employeesQ.data?.employees ?? [];

  return (
    <section>
      <SectionHeader
        title="Team"
        description="People who can sign in to this workspace. Invitations and role changes are managed in HR."
        extra={
          <div className="flex items-center gap-2">
            <Link
              to="/dashboard/settings/access"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-coral text-white text-[12.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Manage access
            </Link>
            <Link
              to="/dashboard/hr/team"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white/80 dark:bg-white/[0.08] text-ink dark:text-white text-[12.5px] font-semibold shadow-pill ring-1 ring-ink/[0.08] dark:ring-white/10 hover:bg-white dark:hover:bg-white/[0.14] transition-colors"
            >
              Manage in HR
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        }
      />
      <GlassCard padding="p-6 sm:p-8">
        {employeesQ.loading ? (
          <LoadingState variant="inline" label="Loading team" />
        ) : employeesQ.error ? (
          <p className="text-[13px] text-rose-600 dark:text-rose-300">
            Couldn’t load team: {employeesQ.error}
          </p>
        ) : employees.length === 0 ? (
          <p className="text-[13px] text-ink/55 dark:text-white/55">
            No teammates yet. Invite people from{' '}
            <Link to="/dashboard/hr/team" className="font-semibold text-coral hover:underline">HR → People</Link>.
          </p>
        ) : (
          <ul className="divide-y divide-ink/8 dark:divide-white/10">
            {employees.map((emp) => (
              <li key={emp.id} className="py-3 first:pt-0 last:pb-0 flex items-center gap-3">
                <Avatar name={emp.name || emp.email} size="md" />
                <div className="flex-1 min-w-0">
                  <p className="text-[13.5px] font-semibold text-ink dark:text-white truncate">
                    {emp.name || emp.email}
                  </p>
                  <p className="text-[12px] text-ink/55 dark:text-white/55 truncate">
                    {emp.email}
                    {emp.designation ? <> · {emp.designation}</> : null}
                    {emp.department ? <> · {emp.department}</> : null}
                  </p>
                </div>
                <Pill variant={emp.is_active ? 'lime' : 'light'} size="sm">
                  {emp.is_active ? 'Active' : 'Inactive'}
                </Pill>
              </li>
            ))}
          </ul>
        )}
      </GlassCard>
    </section>
  );
}

/* ─────────────────────────── MODULES ─────────────────────────── */

const MODULE_LABELS: Record<string, string> = {
  invoicing:  'Invoicing',
  payments:   'Payments',
  expenses:   'Expenses',
  hr:         'HR & People',
  payroll:    'Payroll',
  projects:   'Projects',
  monitoring: 'Monitoring',
  email:      'Email',
};

function ModulesSection() {
  // Read from the ungated enabled-modules endpoint (via EnabledModulesProvider)
  // instead of the billing.view-gated /api/billing/subscription — regular
  // members were getting a permission error here just to SEE what's enabled.
  const { modules, loading } = useEnabledModules();
  const canManageBilling = usePermission('billing.view');
  const enabled = Object.keys(MODULE_LABELS).filter((k) => modules.has(k));

  return (
    <section>
      <SectionHeader
        title="Modules"
        description="Features available in this workspace. To enable or disable modules, manage your subscription."
        extra={
          canManageBilling && (
            <Link
              to="/dashboard/billing"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-coral text-white text-[12.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors"
            >
              Manage in Billing
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          )
        }
      />
      <GlassCard padding="p-6 sm:p-8">
        {loading ? (
          <LoadingState variant="inline" label="Loading modules" />
        ) : enabled.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-[13px] text-ink/55 dark:text-white/55 mb-4">
              No modules are enabled yet.
              {canManageBilling
                ? ' Run the trial wizard to pick what your team needs.'
                : ' Ask the workspace owner to enable the features your team needs.'}
            </p>
            {canManageBilling && (
              <Link
                to="/dashboard/billing/trial-wizard"
                className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-ink-900 text-white dark:bg-coral text-[12.5px] font-semibold shadow-cta hover:bg-ink-800 dark:hover:bg-coral-600 transition-colors"
              >
                Open trial wizard
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {enabled.map((key) => (
              <li
                key={key}
                className="flex items-center gap-3 p-3.5 rounded-2xl bg-white/70 dark:bg-white/[0.05] ring-1 ring-ink/8 dark:ring-white/10"
              >
                <span className="h-8 w-8 rounded-xl bg-lime-neon text-ink flex items-center justify-center shrink-0">
                  <Check className="h-4 w-4" strokeWidth={2.4} />
                </span>
                <div className="min-w-0">
                  <p className="text-[13.5px] font-semibold text-ink dark:text-white truncate">
                    {MODULE_LABELS[key] ?? key}
                  </p>
                  <p className="text-[11.5px] text-ink/55 dark:text-white/55 uppercase tracking-[0.08em]">
                    Enabled
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </GlassCard>
    </section>
  );
}

/* ─────────────────────────── SECURITY ─────────────────────────── */

interface SessionRow {
  id: string;
  user_agent: string | null;
  ip_hash: string | null;
  issued_at: string;
  last_seen_at: string;
  revoked_at: string | null;
  is_current?: boolean;
}

function shortAgent(ua: string | null): string {
  if (!ua) return 'Unknown device';
  if (/Macintosh|Mac OS/i.test(ua)) return 'Mac · ' + browserName(ua);
  if (/Windows/i.test(ua))          return 'Windows · ' + browserName(ua);
  if (/Linux/i.test(ua))            return 'Linux · ' + browserName(ua);
  if (/iPhone|iPad/i.test(ua))      return 'iOS · ' + browserName(ua);
  if (/Android/i.test(ua))          return 'Android · ' + browserName(ua);
  return browserName(ua);
}

function browserName(ua: string): string {
  if (/Edg\//.test(ua))      return 'Edge';
  if (/OPR\//.test(ua))      return 'Opera';
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua))  return 'Firefox';
  if (/Safari\//.test(ua))   return 'Safari';
  return 'Browser';
}

function SecuritySection() {
  const confirm = useConfirm();
  const [busyReset, setBusyReset] = React.useState(false);
  const [busyRevoke, setBusyRevoke] = React.useState(false);
  const [sessions, setSessions] = React.useState<SessionRow[] | null>(null);

  React.useEffect(() => {
    api<{ sessions: SessionRow[] }>('/api/auth/me/sessions')
      .then((r) => setSessions(r.sessions))
      .catch(() => setSessions([]));
  }, []);

  async function changePassword() {
    if (busyReset) return;
    setBusyReset(true);
    try {
      await api('/api/auth/me/password-reset', { method: 'POST', body: {} });
      toast.success('Reset link sent', {
        description: 'Check your email — the link expires in 15 minutes.',
      });
    } catch (err: any) {
      toast.error('Could not send link', { description: err.message ?? String(err) });
    } finally {
      setBusyReset(false);
    }
  }

  async function endAllSessions() {
    if (busyRevoke) return;
    if (!(await confirm({ title: 'Sign out of every device?', description: "You'll need to sign back in here.", destructive: true, confirmLabel: 'Sign out' }))) return;
    setBusyRevoke(true);
    try {
      await api('/api/auth/me/sessions/revoke-all', { method: 'POST', body: {} });
      toast.success('All sessions revoked');
      // Force re-auth: clear token + reload.
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    } catch (err: any) {
      toast.error('Could not revoke', { description: err.message ?? String(err) });
      setBusyRevoke(false);
    }
  }

  async function revokeOne(id: string) {
    try {
      await api(`/api/auth/me/sessions/${id}/revoke`, { method: 'POST', body: {} });
      setSessions((prev) => prev ? prev.map((s) => s.id === id ? { ...s, revoked_at: new Date().toISOString() } : s) : prev);
    } catch (err: any) {
      toast.error('Revoke failed', { description: err.message ?? String(err) });
    }
  }

  return (
    <section>
      <SectionHeader
        title="Security"
        description="Manage how you sign in to this workspace."
      />
      <div className="space-y-4">
        <MfaSettingsCard />

        <GlassCard padding="p-6 sm:p-7">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <Eyebrow className="mb-1.5">Password</Eyebrow>
              <p className="text-[14px] font-semibold text-ink dark:text-white">
                Reset via magic-link email
              </p>
              <p className="text-[12.5px] text-ink/60 dark:text-white/55 mt-1.5 max-w-md leading-relaxed">
                We email a one-time link to {' '}<span className="font-mono">your address</span>. Click it, set a new password.
              </p>
            </div>
            <PdsButton variant="secondary" size="md" onClick={changePassword} disabled={busyReset} className="!h-10 !px-5 !text-[13px]">
              {busyReset ? 'Sending…' : 'Send reset link'}
              <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
            </PdsButton>
          </div>
        </GlassCard>

        <GlassCard padding="p-6 sm:p-7">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <Eyebrow className="mb-1.5">Active sessions</Eyebrow>
              <p className="text-[14px] font-semibold text-ink dark:text-white">
                Recent sign-ins
              </p>
              <p className="text-[12.5px] text-ink/60 dark:text-white/55 mt-1.5 max-w-md leading-relaxed">
                "Sign out everywhere" revokes every session immediately — including this one, so you'll be
                returned to the sign-in page. "Forget device" only removes a sign-in from this list; that
                device stays signed in until its session expires or you sign out everywhere.
              </p>
            </div>
            <PdsButton variant="ghost" size="md" onClick={endAllSessions} disabled={busyRevoke} className="!h-10 !px-5 !text-[13px]">
              {busyRevoke ? 'Revoking…' : 'Sign out everywhere'}
            </PdsButton>
          </div>

          {sessions && sessions.length > 0 && (
            <ul className="mt-5 divide-y divide-ink/8 dark:divide-white/10">
              {sessions.map((s) => {
                const active = s.revoked_at == null;
                return (
                  <li key={s.id} className="py-3 flex items-center gap-3">
                    <span
                      aria-hidden
                      className={cn(
                        'h-2 w-2 rounded-full shrink-0',
                        s.is_current ? 'bg-coral' : active ? 'bg-lime-neon' : 'bg-ink/25 dark:bg-white/25',
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-[13.5px] font-semibold text-ink dark:text-white truncate">
                        {shortAgent(s.user_agent)}
                        {s.is_current && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full bg-coral/15 text-coral-700 dark:text-coral-300 text-[10px] font-bold uppercase tracking-[0.10em]">
                            This device
                          </span>
                        )}
                      </p>
                      <p className="text-[11.5px] text-ink/55 dark:text-white/45 tabular-nums">
                        Signed in {new Date(s.issued_at).toLocaleString()} {s.ip_hash ? `· ${s.ip_hash.slice(0, 8)}` : ''}
                        {!active && ' · revoked'}
                      </p>
                    </div>
                    {active && !s.is_current && (
                      <button
                        type="button"
                        onClick={() => revokeOne(s.id)}
                        title="Removes this sign-in from the list. It stays valid until it expires — use Sign out everywhere to revoke access now."
                        className="text-[12.5px] font-semibold text-ink/65 dark:text-white/55 hover:text-rose-600 dark:hover:text-rose-300 transition-colors"
                      >
                        Forget device
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </GlassCard>
      </div>
    </section>
  );
}

/* ─────────────────────────── NOTIFICATIONS ─────────────────────────── */

// 'reports' was removed: it read "Weekly reports — a Monday digest of the past
// week", and nothing in the API has ever produced such a digest. A toggle for a
// feature that does not exist is the same defect as a toggle nothing reads.
type NotifKey = 'invoices' | 'payments' | 'team';

interface NotifPrefs {
  invoices: boolean;
  payments: boolean;
  team:     boolean;
}

// Everything on by default: a silent default-off means an owner never learns an
// invoice went out. Mirrors DEFAULT_NOTIFICATION_PREFS in api/modules/notifications/prefs.ts.
const NOTIF_DEFAULTS: NotifPrefs = { invoices: true, payments: true, team: true };

const NOTIF_CATEGORIES: Array<{ key: NotifKey; label: string; help: string }> = [
  { key: 'invoices', label: 'Invoices',        help: 'Invoices sent, and recurring invoices generated.' },
  { key: 'payments', label: 'Payments',        help: 'Received, refunded, failed, disputed and unapplied payments.' },
  { key: 'team',     label: 'Team activity',   help: 'Employee invites, leave requests and decisions, finalised payroll.' },
];

// Server (/api/auth/me notification_prefs) is the single source of truth.
// Until it hydrates we show defaults; no localStorage.
function readNotifPrefs(server: Record<string, unknown> | undefined): NotifPrefs {
  const out: NotifPrefs = { ...NOTIF_DEFAULTS };
  if (server && typeof server === 'object') {
    for (const k of Object.keys(NOTIF_DEFAULTS) as NotifKey[]) {
      const v = (server as any)[k];
      if (typeof v === 'boolean') out[k] = v;
    }
  }
  return out;
}

function NotificationsSection() {
  const { identity, refresh } = useAuth();
  const serverPrefs = identity?.type === 'user' ? identity.notification_prefs : undefined;

  const [persisted, setPersisted] = React.useState<NotifPrefs>(() => readNotifPrefs(serverPrefs));
  const [draft, setDraft] = React.useState<NotifPrefs>(persisted);
  const [saving, setSaving] = React.useState(false);

  // Re-hydrate when the server prefs arrive (or change).
  React.useEffect(() => {
    const next = readNotifPrefs(serverPrefs);
    setPersisted(next);
    setDraft(next);
  }, [serverPrefs]);

  const dirty = (Object.keys(draft) as NotifKey[]).some((k) => draft[k] !== persisted[k]);

  function toggle(k: NotifKey) {
    setDraft((d) => ({ ...d, [k]: !d[k] }));
  }

  async function save() {
    if (!dirty) return;
    setSaving(true);
    try {
      await api('/api/auth/me', { method: 'PATCH', body: { notification_prefs: draft } });
      setPersisted(draft);
      toast.success('Notification preferences saved');
      refresh().catch(() => undefined);
    } catch (err: any) {
      toast.error('Save failed', { description: err.message ?? String(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <SectionHeader
        title="Notifications"
        description="Which workspace events you want to hear about. Saved to your account (PATCH /api/auth/me), and carried across every device you sign in from."
      />
      <GlassCard padding="p-6 sm:p-8">
        {/* Honesty note. These preferences are persisted on the user row, but
            nothing on the server currently reads notification_prefs when it
            decides what to send — so a toggle here does not yet suppress an
            email or an in-app notification. Saying so beats a control that
            silently does nothing. Delete this note in the same change that
            makes the sender consult the column. */}
        <p role="note" className="mb-5 text-[12.5px] leading-relaxed text-muted-foreground">
          These control the in-app notifications you receive. Transactional email — invoices,
          receipts, payment reminders, password resets — always sends regardless of what is set
          here, and platform announcements are always delivered.
        </p>
        <ul className="divide-y divide-ink/8 dark:divide-white/10">
          {NOTIF_CATEGORIES.map((cat) => (
            <li key={cat.key} className="py-4 first:pt-0 last:pb-0 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-semibold text-ink dark:text-white">{cat.label}</p>
                <p className="text-[12px] text-ink/55 dark:text-white/55 mt-1 leading-relaxed">{cat.help}</p>
              </div>
              <Switch
                checked={draft[cat.key]}
                onChange={() => toggle(cat.key)}
                ariaLabel={`Toggle ${cat.label} notifications`}
              />
            </li>
          ))}
        </ul>

        <SaveBar dirty={dirty} saving={saving} onSave={save} />
      </GlassCard>
    </section>
  );
}

function Switch({
  checked, onChange, ariaLabel,
}: { checked: boolean; onChange: () => void; ariaLabel?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={onChange}
      className={cn(
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0',
        'focus:outline-none focus:ring-2 focus:ring-coral/40',
        checked
          ? 'bg-coral'
          : 'bg-ink/15 dark:bg-white/20',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'inline-block h-5 w-5 rounded-full bg-white shadow-pill transform transition-transform',
          checked ? 'translate-x-[22px]' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

/* ─────────────────────────── DANGER ZONE ─────────────────────────── */

interface DeletionStatus {
  deletion_requested_at: string | null;
  deletion_requested_by: string | null;
  purge_eligible_at: string | null;
}

function DangerZoneSection() {
  const confirm = useConfirm();
  const { tenant } = useBranding();
  const { identity } = useAuth();
  const tz = identity?.type === 'user' ? identity.timezone : undefined;
  const companyName = tenant?.company_name ?? '';

  const [confirmInput, setConfirmInput] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [deletion, setDeletion] = React.useState<DeletionStatus | null>(null);
  const [loaded, setLoaded] = React.useState(false);

  // The DELETE flow is confirm-gated by typing the workspace name (UX),
  // and the backend additionally requires { confirm: 'DELETE' }.
  const canDelete = confirmInput.trim() === companyName.trim() && companyName.length > 0;
  const scheduled = !!deletion?.deletion_requested_at;

  React.useEffect(() => {
    // NOTE: deletion status lives at the lifecycle router's root (GET /api/tenant).
    api<{ deletion: DeletionStatus }>('/api/tenant', { noRedirectOn401: true })
      .then((r) => setDeletion(r.deletion))
      .catch(() => setDeletion(null))
      .finally(() => setLoaded(true));
  }, []);

  async function requestDelete() {
    if (!canDelete || busy) return;
    if (!(await confirm({ title: `Schedule "${companyName}" for deletion?`, description: 'You can cancel during the grace window.', destructive: true, confirmLabel: 'Delete' }))) return;
    setBusy(true);
    try {
      const r = await api<{ deletion: DeletionStatus }>('/api/tenant', {
        method: 'DELETE',
        body: { confirm: 'DELETE' },
      });
      setDeletion(r.deletion);
      setConfirmInput('');
      toast.success('Workspace scheduled for deletion', {
        description: 'You can cancel any time before the purge date.',
      });
    } catch (err: any) {
      toast.error('Could not schedule deletion', { description: err.message ?? String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function cancelDelete() {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api<{ deletion: DeletionStatus }>('/api/tenant/cancel', { method: 'POST', body: {} });
      setDeletion(r.deletion);
      toast.success('Deletion cancelled', { description: 'Your workspace is safe.' });
    } catch (err: any) {
      toast.error('Could not cancel', { description: err.message ?? String(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <SectionHeader
        title="Danger zone"
        description="Irreversible actions for this workspace. Please double-check everything before continuing."
      />
      <div
        className={cn(
          'rounded-3xl p-6 sm:p-8',
          'bg-rose-500/5 dark:bg-rose-500/10',
          'ring-1 ring-rose-500/25 dark:ring-rose-500/35',
        )}
      >
        <div className="flex items-start gap-3 mb-5">
          <span className="h-9 w-9 rounded-2xl bg-rose-500/15 text-rose-600 dark:text-rose-300 ring-1 ring-rose-500/30 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-[14.5px] font-semibold text-rose-700 dark:text-rose-200">
              Delete workspace
            </p>
            <p className="text-[12.5px] text-rose-700/80 dark:text-rose-200/80 mt-1.5 leading-relaxed max-w-xl">
              Permanently removes every invoice, client, employee record and uploaded file in this workspace.
              Deletion is scheduled with a grace window — you can cancel before the purge runs. This cannot be undone afterward.
            </p>
          </div>
        </div>

        {scheduled ? (
          <div className="rounded-2xl bg-white/60 dark:bg-white/[0.05] ring-1 ring-rose-500/25 p-4 sm:p-5">
            <p className="text-[13.5px] font-semibold text-rose-700 dark:text-rose-200">
              Scheduled for deletion
            </p>
            <p className="text-[12.5px] text-rose-700/80 dark:text-rose-200/80 mt-1.5 leading-relaxed">
              This workspace will be permanently purged on{' '}
              <span className="font-semibold tabular-nums">
                {formatInTimeZone(deletion!.purge_eligible_at, tz)}
              </span>
              {tz ? <span className="opacity-70"> ({tz.replace(/_/g, ' ')})</span> : null}.
              Cancel below to keep your workspace.
            </p>
            <button
              type="button"
              onClick={cancelDelete}
              disabled={busy}
              className={cn(
                'mt-4 inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full',
                'text-[13px] font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-coral/40',
                'bg-ink-900 text-white dark:bg-coral hover:bg-ink-800 dark:hover:bg-coral-600',
                busy && 'opacity-60 cursor-not-allowed',
              )}
            >
              {busy ? 'Working…' : 'Cancel deletion'}
            </button>
          </div>
        ) : (
          <>
            <div className="grid gap-2 max-w-md">
              <FieldLabel htmlFor="confirm-co">
                Type <span className="font-mono text-rose-700 dark:text-rose-200">{companyName || '<workspace name>'}</span> to confirm
              </FieldLabel>
              <PdsInput
                id="confirm-co"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder={companyName}
              />
            </div>

            <button
              type="button"
              onClick={requestDelete}
              disabled={!canDelete || busy || !loaded}
              className={cn(
                'mt-5 inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full',
                'text-[13.5px] font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-rose-500/40',
                canDelete && loaded
                  ? 'bg-rose-600 text-white shadow-[0_8px_22px_-8px_rgba(244,63,94,0.55)] hover:bg-rose-700'
                  : 'bg-rose-500/15 text-rose-700/60 dark:text-rose-300/60 cursor-not-allowed',
              )}
            >
              {busy ? 'Working…' : 'Delete this workspace'}
            </button>
          </>
        )}
      </div>
    </section>
  );
}
